'use es6';

import { useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { FormLabel } from 'foundations-components/transitional/components/FormLabel';
import { SuccessIcon } from 'foundations-assets';
import { Toggle } from 'foundations-components/transitional/components/Toggle';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { List } from 'foundations-components/transitional/components/List';
import { ExpandableText } from 'foundations-components/transitional/components/ExpandableText';
import { Button } from 'foundations-components/transitional/components/Button';
import * as PlanType from 'marketplace-ui-common/constants/pricings/PlanType';
import * as CustomPropTypes from '../../../constants/PropTypes';
import { useVisible } from '../../../utils/hooks/useVisible';
import { Small as FoundationsSmall } from 'foundations-components/transitional/components/Small';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getI18nKey = key => `appMarketplace.detail.pricing.plan.${key}`;
const PLAN_TYPE_PRICING_I18N_KEYS = {
  [PlanType.FREE]: 'free_jsx',
  [PlanType.MONTHLY]: 'monthly_jsx',
  [PlanType.YEARLY]: 'yearly_jsx',
  [PlanType.ONE_TIME]: 'oneTime_jsx',
  [PlanType.METERED]: 'metered_jsx'
};
const CARD_WIDTH = 320;
const CARD_VISIBILITY_OFFSET_FACTOR_RIGHT = 0.55;
const CARD_VISIBILITY_OFFSET_FACTOR_LEFT = 0.5;
const CardHeaderWrapper = styled.div.withConfig({
  displayName: "PricingPlanCard__CardHeaderWrapper",
  componentId: "qg8m1z-0"
})(["background-color:", ";background-image:linear-gradient( -303deg,", ",", " );border-top-left-radius:inherit;border-top-right-radius:inherit;color:", " !important;"], ({
  theme
}) => theme.color['fill-accent-purple-default'], ({
  theme
}) => theme.color['fill-accent-purple-default'], ({
  theme
}) => theme.color['fill-accent-blue-default'], ({
  theme
}) => theme.color['text-core-on-fill-default']);
const Price = props => {
  const {
    children
  } = props;
  return /*#__PURE__*/_jsx(Text, {
    marginBlockEnd: "0",
    typography: "heading-600",
    children: children || /*#__PURE__*/_jsx(FormattedIntlCurrency, Object.assign({}, props))
  });
};
const PricingPlanCard = ({
  currency,
  setVisibleCardIndex,
  onTrackInteraction,
  plan,
  cardIndex
}) => {
  const {
    planType,
    amount,
    contactUsDetails,
    contactUsPlanType,
    contactUsUrl,
    commitmentAmount,
    extraCharges,
    features,
    freeTrialDays,
    meterUnit,
    tagline
  } = plan;
  const [showCommitmentPricing, setShowCommitmentPricing] = useState(!!commitmentAmount);
  const cardRef = useRef(null);
  const visible = useVisible(cardRef, `0px -${CARD_VISIBILITY_OFFSET_FACTOR_RIGHT * CARD_WIDTH}px 0px -${CARD_VISIBILITY_OFFSET_FACTOR_LEFT * CARD_WIDTH}px`);
  useEffect(() => {
    if (visible) {
      setVisibleCardIndex(cardIndex);
    }
  }, [setVisibleCardIndex, visible, cardIndex]);
  const renderedTagline = useMemo(() => {
    if (!tagline) {
      return null;
    }
    return /*#__PURE__*/_jsx(Text, {
      color: "text-core-on-fill-default",
      marginBlockEnd: "0",
      as: "p",
      children: tagline
    });
  }, [tagline]);
  const renderedFreeTrialBadge = useMemo(() => {
    if (!freeTrialDays && planType !== PlanType.CONTACT_US) return null;
    const textKey = freeTrialDays ? 'freeTrialBadgeText' : 'startsAt';
    return /*#__PURE__*/_jsx(View, {
      marginBlockEnd: "-800",
      children: /*#__PURE__*/_jsx(Badge, {
        use: "koala",
        marginBlockStart: "600",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: getI18nKey(textKey),
          options: {
            freeTrialDays
          }
        })
      })
    });
  }, [freeTrialDays, planType]);
  const renderedPrice = useMemo(() => {
    if (planType === PlanType.CONTACT_US && !contactUsPlanType) {
      return null;
    }
    if (planType === PlanType.FREE) {
      return /*#__PURE__*/_jsx(CardSection, {
        paddingBlock: "300",
        paddingInline: "1000",
        marginBlockStart: "400",
        children: /*#__PURE__*/_jsx(Price, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: getI18nKey('pricing.free')
          })
        })
      });
    }
    const pricingI18nKey = planType === PlanType.CONTACT_US ? PLAN_TYPE_PRICING_I18N_KEYS[contactUsPlanType] : PLAN_TYPE_PRICING_I18N_KEYS[planType];
    let value;
    if (planType === PlanType.MONTHLY && showCommitmentPricing) {
      value = commitmentAmount / 12;
    } else {
      value = amount;
    }
    return /*#__PURE__*/_jsx(CardSection, {
      paddingBlock: "300",
      paddingInline: "1000",
      marginBlockStart: "400",
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: getI18nKey(`pricing.${pricingI18nKey}`),
        options: {
          currencyCode: currency,
          simplifyCurrency: true,
          value,
          meterUnit
        },
        elements: {
          Price,
          Small: FoundationsSmall
        }
      })
    });
  }, [amount, contactUsPlanType, commitmentAmount, currency, meterUnit, planType, showCommitmentPricing]);
  const renderedCommitmentPricingToggle = useMemo(() => {
    if (!(planType === PlanType.MONTHLY && commitmentAmount)) {
      return null;
    }
    return /*#__PURE__*/_jsxs(CardSection, {
      paddingBlockEnd: "300",
      paddingInline: "1000",
      children: [/*#__PURE__*/_jsx(FormLabel, {
        paddingInlineEnd: "0",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: getI18nKey('commitmentPricingToggleLabels.monthly')
        })
      }), /*#__PURE__*/_jsx(Toggle, {
        checked: showCommitmentPricing,
        onChange: ({
          target: {
            checked
          }
        }) => {
          setShowCommitmentPricing(checked);
          onTrackInteraction('toggle plan commitment pricing', {
            section: 'pricing',
            value: checked ? 'yearly' : 'monthly'
          });
        },
        size: "extra-small",
        textChecked: "",
        textUnchecked: "",
        marginInline: "300"
      }), /*#__PURE__*/_jsx(FormLabel, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: getI18nKey('commitmentPricingToggleLabels.yearly')
        })
      })]
    });
  }, [planType, commitmentAmount, showCommitmentPricing, onTrackInteraction]);
  const renderedExtraCharges = useMemo(() => {
    if (!extraCharges) {
      return null;
    }
    return /*#__PURE__*/_jsx(CardSection, {
      paddingBlockEnd: "300",
      paddingInline: "1000",
      children: /*#__PURE__*/_jsx(FoundationsSmall, {
        use: "help",
        children: extraCharges
      })
    });
  }, [extraCharges]);
  const renderedFeatures = useMemo(() => {
    if (!features) {
      return null;
    }
    const splitFeatures = features.split(/\r?\n/g).filter(feature => feature.trim());
    return /*#__PURE__*/_jsx(CardSection, {
      paddingBlock: "0",
      paddingInline: "1000",
      children: /*#__PURE__*/_jsx(List, {
        wrapChildren: true,
        children: splitFeatures.map((feature, index) => /*#__PURE__*/_jsx(MediaObject, {
          align: "start",
          itemLeft: /*#__PURE__*/_jsx(SuccessIcon, {
            color: "do-not-use-oz"
          }),
          marginBlockEnd: "200",
          children: feature
        }, `feature-${index}`))
      })
    });
  }, [features]);
  const renderedContactUsButton = useMemo(() => {
    if (planType !== PlanType.CONTACT_US || !contactUsUrl) {
      return null;
    }
    return /*#__PURE__*/_jsx(CardSection, {
      paddingBlock: "300",
      paddingInline: "1000",
      marginBlockStart: "400",
      children: /*#__PURE__*/_jsx(View, {
        inlineSize: "100%",
        children: /*#__PURE__*/_jsx(Button, {
          external: true,
          href: contactUsUrl,
          size: "sm",
          use: "tertiary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: getI18nKey('contactUs')
          })
        })
      })
    });
  }, [contactUsUrl, planType]);
  const renderedContactUsDetails = useMemo(() => {
    if (!contactUsDetails) return null;
    return /*#__PURE__*/_jsx(CardSection, {
      paddingBlock: "300",
      paddingInline: "1000",
      children: contactUsDetails
    });
  }, [contactUsDetails]);

  // @bend-expect trellis-tokenized
  return /*#__PURE__*/_jsx("div", {
    ref: cardRef,
    children: /*#__PURE__*/_jsx(Flex, {
      alignItems: "stretch",
      blockSize: "100%",
      "data-pricing-index": cardIndex,
      "data-test-id": "pricing-plan-card",
      children: /*#__PURE__*/_jsx(CardWrapper, {
        children: /*#__PURE__*/_jsx(View, {
          inlineSize: `${CARD_WIDTH}px`,
          minInlineSize: `${CARD_WIDTH}px`,
          children: /*#__PURE__*/_jsx(ExpandableText, {
            maxHeight: 410,
            expandOnFocus: false,
            children: /*#__PURE__*/_jsxs(View, {
              borderRadius: "100",
              children: [/*#__PURE__*/_jsx(CardHeaderWrapper, {
                children: /*#__PURE__*/_jsxs(Flex, {
                  alignItems: "center",
                  flexDirection: "column",
                  padding: "600",
                  textAlign: "center",
                  children: [/*#__PURE__*/_jsx(Text, {
                    color: "text-core-on-fill-default",
                    marginBlockEnd: renderedTagline ? '200' : '0',
                    typography: "heading-400",
                    children: plan.name
                  }), renderedTagline, renderedFreeTrialBadge]
                })
              }), /*#__PURE__*/_jsxs("div", {
                style: {
                  textAlign: 'center'
                },
                children: [renderedPrice, renderedExtraCharges, renderedCommitmentPricingToggle, renderedContactUsButton]
              }), renderedFeatures, renderedContactUsDetails]
            })
          })
        })
      })
    })
  });
};
PricingPlanCard.propTypes = {
  cardIndex: PropTypes.number,
  currency: PropTypes.string.isRequired,
  onTrackInteraction: PropTypes.func.isRequired,
  plan: PropTypes.shape(CustomPropTypes.ListingPricingCore),
  setVisibleCardIndex: PropTypes.func.isRequired
};
export default PricingPlanCard;